import Button from 'components/ui/button';
import { FOCO_VISIVEL } from 'utils/foco';

import { ExperienciaEscolhida } from '../feedbackPage/experiencia-escolhida';
import { ArteDoCartao } from './CartaoDeExperiencia';

interface ExperienciaConfirmadaProps {
  experiencia: ExperienciaEscolhida;
  regulamentoUrl: string;
  /** Prazo do ciclo, já formatado. */
  prazo: string;
  /** O prazo de cancelamento já passou (relógio do servidor). */
  prazoExpirado: boolean;
  /** O botão de cancelar aparece só quando há resgate ativo e prazo conhecido. */
  podeCancelar: boolean;
  onRegras: () => void;
  onCancelar: () => void;
  /** Volta para a vitrine do ciclo (TRC-24). */
  onVerCatalogo: () => void;
}

/**
 * "Parabéns! Você escolheu viver uma experiência." e, ao lado, o cartão da escolha com o
 * cancelamento no pé (CAT-31, CAT-32; DESF-01, DESF-01.1). O cartão é a casca da vitrine (D-14).
 */
export default function ExperienciaConfirmada({
  experiencia,
  regulamentoUrl,
  prazo,
  prazoExpirado,
  podeCancelar,
  onRegras,
  onCancelar,
  onVerCatalogo,
}: ExperienciaConfirmadaProps) {
  const temBotao = podeCancelar || prazoExpirado;

  return (
    <section aria-labelledby="titulo-experiencia-confirmada" className="mb-6 flex flex-col gap-[22px] md:flex-row md:items-start">
      <div className="min-w-0 flex-1 rounded-xl bg-brand-white px-6 py-6 shadow-jj-01">
        <h2
          id="titulo-experiencia-confirmada"
          tabIndex={0}
          className={`m-0 mb-4 font-display text-[22px] font-extrabold leading-[1.2] text-brand-blue-dark sm:text-[24px] ${FOCO_VISIVEL}`}
        >
          Parabéns! Você escolheu viver uma experiência.
        </h2>
        <p tabIndex={0} className={`m-0 text-[15px] leading-[1.6] text-content ${FOCO_VISIVEL}`}>
          Experiência confirmada:{' '}
          <strong className="text-brand-blue-dark">
            {experiencia.quantidade && <>{experiencia.quantidade} </>}
            {experiencia.escolha}
          </strong>
          {experiencia.valorDoCartao && <span className="block">valor: {experiencia.valorDoCartao}</span>}
        </p>
        {experiencia.aviso && (
          <p role="note" className="m-0 mt-3 rounded-lg bg-brand-yellow-light px-3 py-2 text-[13px] leading-[1.5] text-content">
            {experiencia.aviso}
          </p>
        )}
        <p tabIndex={0} className={`m-0 mt-3 text-[13px] text-content ${FOCO_VISIVEL}`}>
          Data da confirmação:{' '}
          <strong>{experiencia.data ? experiencia.data.toLocaleDateString('pt-BR') : 'Carregando...'}</strong>
        </p>
        <p className="m-0 mt-3 text-[13px] text-content">
          Ver{' '}
          <button
            type="button"
            onClick={onRegras}
            aria-haspopup="dialog"
            className={`cursor-pointer border-0 bg-transparent p-0 font-text text-[13px] font-bold text-brand-blue-dark underline ${FOCO_VISIVEL}`}
          >
            regras de uso
          </button>{' '}
          e{' '}
          <a
            href={regulamentoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`font-bold text-brand-blue-dark underline ${FOCO_VISIVEL}`}
          >
            regulamento<span className="sr-only"> (abre em nova aba)</span>
          </a>
          .
        </p>
        <div className="mt-5">
          <Button variant="secundario" onClick={onVerCatalogo}>
            Ver experiências do ciclo
          </Button>
        </div>
      </div>

      <aside
        aria-label="Experiência escolhida"
        className="flex w-full shrink-0 flex-col overflow-hidden rounded-xl bg-brand-white shadow-jj-01 md:w-[318px]"
      >
        <ArteDoCartao tipo={experiencia.tipo} imagem={experiencia.imagem} />
        <div className="px-5 pb-4 pt-[18px]">
          <h3 className="m-0 font-display text-[20px] font-extrabold leading-[1.15] text-brand-blue-dark">{experiencia.nome}</h3>
          {experiencia.descricao && <p className="m-0 mt-3 text-[13px] leading-[1.6] text-content">{experiencia.descricao}</p>}
        </div>
        {temBotao && (
          // DESF-01 e D-13: o prazo fica no pé do cartão, junto do botão de cancelar.
          <>
            <p
              tabIndex={0}
              className={`m-0 mx-5 mb-4 mt-auto rounded-lg bg-surface-100 px-4 py-3 text-[13px] leading-[1.5] text-content ${FOCO_VISIVEL}`}
            >
              {prazoExpirado ? (
                <>
                  O prazo para cancelamento desta experiência expirou em <strong>{prazo}</strong>. Não é mais possível cancelar.
                </>
              ) : (
                <>
                  Você pode cancelar esta experiência até <strong>{prazo}</strong>. Após essa data não será mais possível
                  cancelar.
                </>
              )}
            </p>
            <button
              type="button"
              onClick={onCancelar}
              disabled={prazoExpirado}
              className={`shrink-0 border-0 border-t border-dashed border-surface-200 px-3 py-[15px] font-text text-[12px] font-bold uppercase tracking-[0.04em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-brand-blue-dark ${
                prazoExpirado
                  ? 'cursor-not-allowed bg-surface-200 text-content-soft'
                  : 'cursor-pointer bg-brand-yellow text-brand-blue-dark hover:bg-button-primary-hover'
              }`}
            >
              {prazoExpirado ? 'Cancelamento indisponível' : 'Cancelar experiência'}
            </button>
          </>
        )}
      </aside>
    </section>
  );
}
